import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import { withRouter, useHistory } from 'react-router-dom';
import moment from 'moment';
import * as XLSX from 'xlsx';
import Page from '../../components/Page/Page';
import { UserFilter } from '../../components';
import { NetworkContext } from '../../context/NetworkContext';
import { SnackBarContext } from '../../context/SnackbarContext';
import { hasPermission } from '../../utils/permissions';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Typography,
  IconButton,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import GetAppIcon from '@material-ui/icons/GetApp';
import DateRangeIcon from '@material-ui/icons/DateRange';
import SearchIcon from '@material-ui/icons/Search';

const STATUS_COLORS = {
  active: 'primary',
  expiring_soon: 'secondary',
  grace: 'default',
  blocked: 'default',
};

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'expiring_soon', label: 'Expiring Soon' },
  { value: 'grace', label: 'Grace' },
  { value: 'blocked', label: 'Blocked' },
];

const SUBSCRIPTION_CSV_HEADERS = [
  { label: 'User ID', key: 'user_profile_id' },
  { label: 'Name', key: 'name' },
  { label: 'Mobile Number', key: 'mobilenumber' },
  { label: 'Company Name', key: 'companyname' },
  { label: 'Email', key: 'email' },
  { label: 'City', key: 'city' },
  { label: 'District', key: 'district' },
  { label: 'State', key: 'state' },
  { label: 'Platform', key: 'platform' },
  { label: 'GSTIN', key: 'gstin' },
  { label: 'Plan', key: 'plan' },
  { label: 'Amount', key: 'amount' },
  { label: 'Valid From', key: 'valid_from' },
  { label: 'Valid Until', key: 'valid_until' },
  { label: 'Status', key: 'status' },
  { label: 'Is Trial', key: 'is_trial' },
  { label: 'Last Login', key: 'last_login' },
  { label: 'Member Since', key: 'member_since' },
];

const GST_PAYMENT_COLUMNS = [
  { label: 'Invoice No', key: 'invoice_number' },
  { label: 'Payment Date', key: 'payment_date' },
  { label: 'User ID', key: 'user_profile_id' },
  { label: 'Name', key: 'name' },
  { label: 'Mobile', key: 'mobilenumber' },
  { label: 'Company', key: 'companyname' },
  { label: 'Email', key: 'email' },
  { label: 'GSTIN', key: 'gstin' },
  { label: 'GST Address', key: 'gst_address' },
  { label: 'State', key: 'state' },
  { label: 'Plan', key: 'plan' },
  { label: 'Coupon Code', key: 'coupon_code' },
  { label: 'Original Amount', key: 'original_amount' },
  { label: 'Discount', key: 'discount_amount' },
  { label: 'Taxable Value', key: 'taxable_value' },
  { label: 'CGST', key: 'cgst' },
  { label: 'SGST', key: 'sgst' },
  { label: 'IGST', key: 'igst' },
  { label: 'Total Amount', key: 'total_amount' },
  { label: 'Payment Mode', key: 'payment_mode' },
  { label: 'Razorpay Payment ID', key: 'razorpay_payment_id' },
  { label: 'Razorpay Order ID', key: 'razorpay_order_id' },
  { label: 'Valid From', key: 'valid_from' },
  { label: 'Valid Until', key: 'valid_until' },
  { label: 'Notes', key: 'notes' },
];

const GST_TABLE_COLUMNS = [
  { label: 'Invoice No', key: 'invoice_number' },
  { label: 'Payment Date', key: 'payment_date' },
  { label: 'Name', key: 'name' },
  { label: 'Mobile', key: 'mobilenumber' },
  { label: 'GSTIN', key: 'gstin' },
  { label: 'Plan', key: 'plan' },
  { label: 'Taxable', key: 'taxable_value' },
  { label: 'CGST', key: 'cgst' },
  { label: 'SGST', key: 'sgst' },
  { label: 'IGST', key: 'igst' },
  { label: 'Total', key: 'total_amount' },
];

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    maxWidth: '100%',
    padding: theme.spacing(3),
  },
  filterRow: {
    display: 'flex',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  filter: {
    minWidth: 200,
    marginTop: theme.spacing(1),
  },
  gstOpenButton: {
    marginTop: theme.spacing(1),
  },
  dialogTitle: {
    paddingBottom: theme.spacing(1),
  },
  dialogSubtitle: {
    marginTop: theme.spacing(0.5),
  },
  filterCard: {
    backgroundColor: theme.palette.background.default,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  filterHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  filterHeaderIcon: {
    color: theme.palette.primary.main,
  },
  dialogFilters: {
    display: 'flex',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  dateField: {
    minWidth: 190,
    backgroundColor: theme.palette.background.paper,
  },
  getButton: {
    minWidth: 120,
    height: 40,
  },
  previewHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  previewMeta: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  dialogTableWrap: {
    maxHeight: 360,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
  },
  tableHeadCell: {
    backgroundColor: theme.palette.background.default,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  amountCell: {
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 220,
    padding: theme.spacing(4),
    textAlign: 'center',
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: 8,
    backgroundColor: theme.palette.background.default,
  },
  emptyIcon: {
    fontSize: 42,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.5),
    opacity: 0.7,
  },
  loadingWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 220,
    gap: theme.spacing(1.5),
  },
  dialogActions: {
    padding: theme.spacing(2),
    justifyContent: 'space-between',
  },
  actionHint: {
    maxWidth: 360,
  },
  results: {
    marginTop: theme.spacing(2),
  },
}));

const MONEY_KEYS = new Set([
  'taxable_value',
  'cgst',
  'sgst',
  'igst',
  'total_amount',
  'original_amount',
  'discount_amount',
]);

function formatMoney(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return '—';
  }
  return `₹${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatCell(key, value) {
  if (MONEY_KEYS.has(key)) {
    return formatMoney(value);
  }
  if (value == null || value === '') {
    return '—';
  }
  return value;
}

export const SubscriptionUsers = withRouter(() => {
  const classes = useStyles();
  const history = useHistory();
  const { sendNetworkRequest } = React.useContext(NetworkContext);
  const snackbarCtx = React.useContext(SnackBarContext);
  const canExport = hasPermission('export_result');
  const [users, setUsers] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [filterparams, setFilterparams] = useState({});
  const [masters, setMasters] = useState({});
  const [downloadusers, setDownloadusers] = useState([]);
  const [ispreparedownload, setIspreparedownload] = useState(false);
  const [gstDialogOpen, setGstDialogOpen] = useState(false);
  const [gstDateFrom, setGstDateFrom] = useState('');
  const [gstDateTo, setGstDateTo] = useState('');
  const [gstRows, setGstRows] = useState([]);
  const [gstReady, setGstReady] = useState(false);
  const [gstLoading, setGstLoading] = useState(false);

  function showSnack(type, msg) {
    if (!snackbarCtx || !snackbarCtx.setSnack) {
      return;
    }
    snackbarCtx.setSnack({
      ...snackbarCtx,
      open: true,
      type,
      msg,
      vertical: 'top',
    });
  }

  function onFilter(filterobj) {
    setIspreparedownload(false);
    setDownloadusers([]);
    const next = { ...(filterobj || {}) };
    if (Object.prototype.hasOwnProperty.call(next, 'membership_status')) {
      setStatusFilter(next.membership_status || 'all');
      delete next.membership_status;
    }
    setFilterparams(prev => ({
      ...prev,
      ...next,
    }));
  }

  function onSearch(searchtext) {
    setIspreparedownload(false);
    setDownloadusers([]);
    setFilterparams(prev => ({
      ...prev,
      searchtext,
    }));
  }

  function onStatusChange(value) {
    setIspreparedownload(false);
    setDownloadusers([]);
    setStatusFilter(value);
  }

  async function loadUsers() {
    const result = await sendNetworkRequest('/admin/listsubscriptionusers', {}, {
      ...filterparams,
      status_filter: statusFilter === 'all' ? null : statusFilter,
    }, false);
    setUsers(result?.users || []);
  }

  async function onPreparedownload() {
    const result = await sendNetworkRequest('/admin/downloadsubscriptionusers', {}, {
      ...filterparams,
      status_filter: statusFilter === 'all' ? null : statusFilter,
    }, false);
    setDownloadusers([...(result?.rows || [])]);
    setIspreparedownload(true);
  }

  function resetGstDialogState() {
    setGstDateFrom('');
    setGstDateTo('');
    setGstRows([]);
    setGstReady(false);
    setGstLoading(false);
  }

  function openGstDialog() {
    resetGstDialogState();
    setGstDialogOpen(true);
  }

  function closeGstDialog() {
    setGstDialogOpen(false);
    resetGstDialogState();
  }

  function onGstDateChange(setter) {
    return (event) => {
      setter(event.target.value);
      setGstReady(false);
      setGstRows([]);
    };
  }

  async function onGetGstPayments() {
    if (!gstDateFrom || !gstDateTo) {
      showSnack('error', 'Select both From and To payment dates');
      return;
    }
    if (moment(gstDateFrom).isAfter(moment(gstDateTo), 'day')) {
      showSnack('error', 'From date cannot be after To date');
      return;
    }
    setGstLoading(true);
    setGstReady(false);
    try {
      const result = await sendNetworkRequest('/admin/downloadsubscriptionpayments', {}, {
        date_from: gstDateFrom,
        date_to: gstDateTo,
      }, false);
      if (!result || result.status === 'error' || result.message === 'Authentication required') {
        showSnack('error', result?.message || 'Unable to load GST payments');
        setGstRows([]);
        setGstReady(false);
        return;
      }
      if (result.statusCode && result.statusCode >= 400) {
        showSnack('error', result.message || 'Unable to load GST payments');
        setGstRows([]);
        setGstReady(false);
        return;
      }
      setGstRows([...(result.rows || [])]);
      setGstReady(true);
    } finally {
      setGstLoading(false);
    }
  }

  function onDownloadGstExcel() {
    if (!gstReady) {
      return;
    }
    const sheetRows = gstRows.map((row) => {
      const out = {};
      GST_PAYMENT_COLUMNS.forEach((col) => {
        out[col.label] = row[col.key] != null ? row[col.key] : '';
      });
      return out;
    });
    const worksheet = XLSX.utils.json_to_sheet(sheetRows.length ? sheetRows : [{}]);
    if (!sheetRows.length) {
      XLSX.utils.sheet_add_aoa(worksheet, [GST_PAYMENT_COLUMNS.map((c) => c.label)], { origin: 'A1' });
    }
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'GST Payments');
    const filename = `subscription-payments-gst-${(gstDateFrom || '').replace(/-/g, '')}-${(gstDateTo || '').replace(/-/g, '')}.xlsx`;
    XLSX.writeFile(workbook, filename);
  }

  const gstTotalAmount = gstRows.reduce((sum, row) => sum + (Number(row.total_amount) || 0), 0);
  const canGetGst = Boolean(gstDateFrom && gstDateTo) && !gstLoading;
  const rangeLabel = gstDateFrom && gstDateTo
    ? `${moment(gstDateFrom).format('DD MMM YYYY')} – ${moment(gstDateTo).format('DD MMM YYYY')}`
    : '';

  async function fetchmasters() {
    const commodities = await sendNetworkRequest('/admin/getparentcommodity', {}, {}, true);
    const master_commodities = [];
    (commodities?.module || []).forEach((item) => {
      master_commodities.push({
        value: item?.id,
        label: item?.name,
      });
    });
    const languages = await sendNetworkRequest('/admin/fetchmasterlanguage', {}, {}, false);
    const master_languages = [];
    (languages || []).forEach((item) => {
      master_languages.push({
        value: item?.id,
        label: item?.name,
      });
    });
    const usertypes = await sendNetworkRequest('/admin/fetchusertype', {}, {}, false);
    setMasters({
      commodity: master_commodities,
      languages: master_languages,
      usertypes: usertypes || [],
    });
  }

  useEffect(() => {
    fetchmasters();
  }, []);

  useEffect(() => {
    loadUsers();
  }, [filterparams, statusFilter]);

  return (
    <Page className={classes.root} title="Subscription Users">
      <Typography variant="h4" gutterBottom>Subscription Overview</Typography>
      <div className={classes.filterRow}>
        <UserFilter
          masters={masters}
          onPrepare={onPreparedownload}
          onSearch={onSearch}
          isreadyfordownload={ispreparedownload}
          onFilter={onFilter}
          data={downloadusers}
          csvHeaders={SUBSCRIPTION_CSV_HEADERS}
          csvFilename="subscription-users"
          statusOptions={STATUS_OPTIONS}
          statusValue={statusFilter}
        />
        <FormControl variant="outlined" className={classes.filter} size="small">
          <InputLabel>Status filter</InputLabel>
          <Select
            value={statusFilter}
            onChange={e => onStatusChange(e.target.value)}
            label="Status filter"
          >
            {STATUS_OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {canExport && (
          <Button
            className={classes.gstOpenButton}
            color="primary"
            size="small"
            variant="outlined"
            startIcon={<GetAppIcon />}
            onClick={openGstDialog}
          >
            Download GST Payments
          </Button>
        )}
      </div>

      <Dialog
        open={gstDialogOpen}
        onClose={closeGstDialog}
        maxWidth="lg"
        fullWidth
        scroll="paper"
      >
        <DialogTitle disableTypography className={classes.dialogTitle}>
          <Typography variant="h5">GST payment export</Typography>
          <Typography variant="body2" color="textSecondary" className={classes.dialogSubtitle}>
            Choose a payment date range, preview successful invoices, then download Excel for GST filing.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Box className={classes.filterCard}>
            <div className={classes.filterHeader}>
              <DateRangeIcon className={classes.filterHeaderIcon} fontSize="small" />
              <Typography variant="subtitle2">1. Select payment period</Typography>
            </div>
            <div className={classes.dialogFilters}>
              <TextField
                className={classes.dateField}
                label="From date"
                type="date"
                size="small"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                value={gstDateFrom}
                onChange={onGstDateChange(setGstDateFrom)}
              />
              <TextField
                className={classes.dateField}
                label="To date"
                type="date"
                size="small"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                value={gstDateTo}
                onChange={onGstDateChange(setGstDateTo)}
              />
              <Button
                className={classes.getButton}
                color="primary"
                variant="contained"
                startIcon={gstLoading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                disabled={!canGetGst}
                onClick={onGetGstPayments}
              >
                {gstLoading ? 'Loading…' : 'Get results'}
              </Button>
            </div>
          </Box>

          <div className={classes.filterHeader}>
            <Typography variant="subtitle2">2. Preview results</Typography>
            {rangeLabel && gstReady && (
              <Typography variant="caption" color="textSecondary">
                {rangeLabel}
              </Typography>
            )}
          </div>

          {gstLoading && (
            <div className={classes.loadingWrap}>
              <CircularProgress size={36} />
              <Typography variant="body2" color="textSecondary">
                Fetching successful payments…
              </Typography>
            </div>
          )}

          {!gstLoading && !gstReady && (
            <div className={classes.emptyState}>
              <DateRangeIcon className={classes.emptyIcon} />
              <Typography variant="subtitle1" gutterBottom>
                No preview yet
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Pick From and To dates, then click <strong>Get results</strong> to load payments here.
              </Typography>
            </div>
          )}

          {!gstLoading && gstReady && (
            <>
              <div className={classes.previewHeader}>
                <div className={classes.previewMeta}>
                  <Chip
                    size="small"
                    color="primary"
                    label={`${gstRows.length} payment${gstRows.length === 1 ? '' : 's'}`}
                  />
                  <Typography variant="body2" color="textSecondary">
                    Total collected {formatMoney(gstTotalAmount)}
                  </Typography>
                </div>
                <Typography variant="caption" color="textSecondary">
                  Only successful online and offline payments are included
                </Typography>
              </div>
              <TableContainer className={classes.dialogTableWrap}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {GST_TABLE_COLUMNS.map((col) => (
                        <TableCell key={col.key} className={classes.tableHeadCell}>
                          {col.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {gstRows.map((row) => (
                      <TableRow hover key={`${row.invoice_number}-${row.user_profile_id}`}>
                        {GST_TABLE_COLUMNS.map((col) => (
                          <TableCell
                            key={col.key}
                            className={MONEY_KEYS.has(col.key) ? classes.amountCell : undefined}
                          >
                            {formatCell(col.key, row[col.key])}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                    {gstRows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={GST_TABLE_COLUMNS.length} align="center">
                          No successful payments in this date range
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Typography variant="caption" color="textSecondary" className={classes.actionHint}>
            {gstReady
              ? '3. Download the Excel file when the preview looks correct.'
              : 'Download becomes available after you load results.'}
          </Typography>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <Button onClick={closeGstDialog}>
              Cancel
            </Button>
            <Button
              color="primary"
              variant="contained"
              startIcon={<GetAppIcon />}
              onClick={onDownloadGstExcel}
              disabled={!gstReady || gstLoading}
            >
              Download Excel
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <TableContainer component={Paper} className={classes.results}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Mobile</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell>Start</TableCell>
              <TableCell>Expiry</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map(row => (
              <TableRow key={row.user_profile_id}>
                <TableCell>{row.user?.name || row.user_name}</TableCell>
                <TableCell>{row.user?.mobilenumber || row.mobile}</TableCell>
                <TableCell>{row.plan?.name || '—'}</TableCell>
                <TableCell>
                  {row.valid_from ? moment(row.valid_from).format('DD/MM/YYYY') : '—'}
                </TableCell>
                <TableCell>
                  {row.valid_until ? moment(row.valid_until).format('DD/MM/YYYY') : '—'}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={row.status}
                    color={STATUS_COLORS[row.status] || 'default'}
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => history.push(`/edituser/${row.user_profile_id}/1`)}
                  >
                    <EditIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {users.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">No subscription records found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Page>
  );
});

export default SubscriptionUsers;
